import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-6">
      <div className="app-bg" />
      <div className="grid lg:grid-cols-2 gap-10 max-w-6xl w-full items-center">
        {/* Left showcase */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="hidden lg:block space-y-6"
        >
          <BrandLogo className="w-full max-w-md h-auto" testid="brand-logo-hero" />
          <p className="text-muted-foreground text-lg max-w-md">
            Simpel unifies attendance, productivity, project health, budget, git activity and risk into a single AI-native workspace.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4">
            {[
              ["16", "Modules"],
              ["100+", "KPIs tracked"],
              ["24/7", "AI insights"],
            ].map(([n, l]) => (
              <div key={l} className="glass rounded-xl p-4">
                <div className="number text-2xl font-medium">{n}</div>
                <div className="text-xs text-muted-foreground mt-1">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-strong rounded-3xl p-8 md:p-10"
        >
          <div className="mb-6">
            <BrandLogo className="h-12 w-auto" testid="brand-logo-form" />
          </div>

          <h2 className="font-display font-semibold text-2xl tracking-tight">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Sign in to your Simpel workspace.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 mt-1.5"
                required
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 mt-1.5"
                required
              />
            </div>
            <Button
              data-testid="login-submit"
              type="submit"
              className="w-full h-11 rounded-lg text-sm font-medium"
              disabled={loading}
            >
              {loading ? "Signing in…" : (
                <>Sign in <ArrowRight className="w-4 h-4 ml-1.5" /></>
              )}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
